import { useContext, useEffect, useMemo, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { PERSONNEL_TYPE_LABEL_KEYS } from '../../constants/personnel';
import { PlantLayoutContext } from '../../index';
import { deleteDataFromApi } from '../../services/Deletes/deleteData';
import { fetchAllDataGet_byApiName } from '../../services/gets/getdata';
import { sendAllDataPost_byApiNameAndData } from '../../services/posts/postdata';
import allPersonnelColumns, { PersonnelDetails } from '../../types/allPersonnelMasterDatatype';
import { AllPersonnelProps } from './all-personnel';
import help from './all-personnel.help';
import translations from './i18n';
import { PersonnelFormValues } from './personnel-form';
import { apiUriMasterDataHttp, apiUriPersonnelHttp } from '../../services/conf/apiConf';

const EMPTY_FORM_VALUES: PersonnelFormValues = {
  isActive: true,
  personnelName: '',
  personnelEmail: '',
  designation: '',
  department: '',
  flagInternal: undefined,
  address1: '',
  address2: '',
  address3: '',
  province: '',
  state: '',
  continent: '',
  country: '',
  zipCode: '',
  contactNo1: '',
  contactNo2: '',
  entityCode: '',
};

type FormMode = 'add' | 'edit' | null;

function useAllPersonnelHook(props: AllPersonnelProps) {
  useResourceBundle(translations, undefined, 'cd5c05d2-8ec5-4a44-a981-ccc5cc179bd1');
  useHelpBundle(help);

  const { t } = useTranslation();

  const [personnelRows, setPersonnelRows] = useState<PersonnelDetails[]>([]);
  const [columnNames] = useState(allPersonnelColumns());
  const [companyId, setCompanyId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [pageLoading, setPageLoading] = useState<boolean>(false);

  const [formMode, setFormMode] = useState<FormMode>(null);
  const [formValues, setFormValues] = useState<PersonnelFormValues>(EMPTY_FORM_VALUES);

  const plantLayoutData = useContext(PlantLayoutContext);
  // Memoized: the Select dropdown (Ant Design's virtualized list) re-measures based on its
  // options' identity, so handing it a brand-new array every render causes an infinite
  // update loop the instant the dropdown opens (React error #185). Only recompute when the
  // underlying plant layout data or company name actually changes.
  const uniqueEntities = useMemo(() => {
    const internalEntities =
      plantLayoutData?.layoutData?.flatMap((item) => item.consumables || [])?.map((item) => item.entity) || [];
    return Array.from(new Set([companyName, ...internalEntities].filter(Boolean)));
  }, [plantLayoutData, companyName]);

  // Memoized for the same reason as uniqueEntities above - a fresh object every render feeds
  // the Select dropdown an unstable options identity and triggers an infinite update loop
  // as soon as it opens.
  const flagOptions: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(Object.entries(PERSONNEL_TYPE_LABEL_KEYS).map(([value, labelKey]) => [value, t(labelKey)])),
    [t],
  );

  const fetchCompanies = (idToFilter: string) => {
    fetchAllDataGet_byApiName('GetMasterCompanies', apiUriMasterDataHttp())
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        const match = rows.find((item: any) => item.id === idToFilter);
        if (match) {
          setCompanyName(match.companyname);
        }
      })
      .catch((error) => {
        console.log('Failed to Fetch companies', error);
      });
  };

  const fetchMasterPersonnelData = () => {
    setPageLoading(true);
    return fetchAllDataGet_byApiName('GetMasterPersonnel', apiUriPersonnelHttp())
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setPersonnelRows(rows);
        return rows;
      })
      .catch((error) => {
        console.log('Failed to Fetch personnel data', error);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  useEffect(() => {
    fetchMasterPersonnelData();

    let effectiveCompanyId = companyId;
    if (!effectiveCompanyId) {
      const storedData = sessionStorage.getItem('companyID');
      if (storedData) {
        effectiveCompanyId = storedData;
        setCompanyId(storedData);
      }
    }

    if (effectiveCompanyId) {
      fetchCompanies(effectiveCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // stateData for the grid: personnel rows plus display-only fields (translated type label,
  // entityCode falling back to the legacy remarks column until the DB backfill has run everywhere).
  const stateData = useMemo(
    () =>
      personnelRows.map((row) => {
        const labelKey = PERSONNEL_TYPE_LABEL_KEYS[row.flagInternal as keyof typeof PERSONNEL_TYPE_LABEL_KEYS];
        return {
          ...row,
          flagInternalLabel: labelKey ? t(labelKey) : '',
          entityCode: row.entityCode || row.remarks,
        };
      }),
    [personnelRows, t],
  );

  const emailError = useMemo(() => {
    if (!formValues.personnelEmail) return '';
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(formValues.personnelEmail) ? '' : 'Invalid email format';
  }, [formValues.personnelEmail]);

  const updateFormField = <K extends keyof PersonnelFormValues>(field: K, value: PersonnelFormValues[K]) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const digitsOnlyFieldChange = (field: 'zipCode' | 'contactNo1' | 'contactNo2', pattern: RegExp) => (value: string) => {
    if (pattern.test(value)) {
      updateFormField(field, value);
    }
  };

  const handleZipCodeChange = digitsOnlyFieldChange('zipCode', /^\d*$/);
  const handleContactNo1Change = digitsOnlyFieldChange('contactNo1', /^[\d+]*$/);
  const handleContactNo2Change = digitsOnlyFieldChange('contactNo2', /^[\d+]*$/);

  const openAddModal = () => {
    setFormValues(EMPTY_FORM_VALUES);
    setFormMode('add');
  };

  const openEditModal = (rowIndex: number | null | undefined) => {
    const row = rowIndex != null ? personnelRows[rowIndex] : undefined;
    if (!row) return;
    setFormValues({
      id: row.id,
      companyId: row.companyId ?? undefined,
      isActive: row.isActive,
      personnelName: row.personnelName,
      personnelEmail: row.personnelEmail,
      designation: row.designation,
      department: row.department,
      flagInternal: row.flagInternal,
      address1: row.address1,
      address2: row.address2,
      address3: row.address3,
      province: row.province,
      state: row.state,
      continent: row.continent,
      country: row.country,
      zipCode: row.zipCode,
      contactNo1: row.contactNo1,
      contactNo2: row.contactNo2,
      entityCode: row.entityCode || row.remarks || '',
    });
    setFormMode('edit');
  };

  const closeModal = () => {
    setFormMode(null);
    setFormValues(EMPTY_FORM_VALUES);
  };

  const handleSaveForm = () => {
    if (!formMode) return;
    const payload = {
      id: formValues.id,
      companyId: formMode === 'add' ? companyId : formValues.companyId,
      personnelName: formValues.personnelName,
      personnelEmail: formValues.personnelEmail,
      designation: formValues.designation,
      department: formValues.department,
      address1: formValues.address1,
      address2: formValues.address2,
      address3: formValues.address3,
      province: formValues.province,
      state: formValues.state,
      country: formValues.country,
      continent: formValues.continent,
      zipCode: formValues.zipCode,
      contactNo1: formValues.contactNo1,
      contactNo2: formValues.contactNo2,
      isActive: formValues.isActive,
      entityCode: formValues.entityCode,
      flagInternal: formValues.flagInternal,
    };

    setPageLoading(true);
    sendAllDataPost_byApiNameAndData('AddOrUpdatePersonnel', payload, apiUriPersonnelHttp())
      .then((response) => {
        if (response) {
          fetchMasterPersonnelData().then(() => {
            notification.success('Success !', 'Data Saved Successfully', undefined, 'medium', 3);
          });
        }
      })
      .catch((error) => {
        console.log(error);
        notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
      })
      .finally(() => {
        closeModal();
        setPageLoading(false);
      });
  };

  const handleDeletePersonnel = (data: any) => {
    deleteDataFromApi(`DeletePersonnel?personnelid=${data.id}`, apiUriPersonnelHttp())
      .then((response) => {
        if (response) {
          fetchMasterPersonnelData().then(() => {
            notification.success('Removed !', 'Data Deleted Permanently', undefined, 'medium', 3);
          });
        }
      })
      .catch((error) => {
        console.log('Failed to delete personnel', error);
      });
  };

  return {
    t,
    flagOptions,
    uniqueEntities,
    columnNames,
    stateData,
    pageLoading,
    formMode,
    formValues,
    emailError,
    updateFormField,
    handleZipCodeChange,
    handleContactNo1Change,
    handleContactNo2Change,
    openAddModal,
    openEditModal,
    closeModal,
    handleSaveForm,
    handleDeletePersonnel,
  };
}

export default useAllPersonnelHook;
